import { createFileRoute, Link } from "@tanstack/react-router";
import { PageShell, PageHero } from "@/components/site/PageShell";
import { SERVICES, SITE_URL } from "@/lib/site";
import { seoMeta, canonicalLink, robotsMeta } from "@/lib/seo";

const DEFAULT_TITLE = "Servicios BTL: activaciones, eventos y promotoras | Improve Yourself";
const DEFAULT_DESCRIPTION =
  "Compara nuestros servicios BTL en Lima: activaciones, eventos, promotoras, anfitrionas, sampling, trade marketing y volanteo. Elige el formato para tu campaña.";

export const Route = createFileRoute("/servicios")({
  component: ServicesIndex,
  head: ({ match }) => {
    const override = match.context.seoOverride;
    return {
      meta: [
        ...seoMeta({
          title: override?.metaTitle ?? DEFAULT_TITLE,
          description: override?.metaDescription ?? DEFAULT_DESCRIPTION,
          path: "/servicios",
          image: override?.ogImage ?? undefined,
        }),
        ...robotsMeta(override?.robotsIndex ?? true, override?.robotsFollow ?? true),
      ],
      links: canonicalLink(override?.canonicalPath ?? "/servicios"),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Inicio", item: `${SITE_URL}/` },
              {
                "@type": "ListItem",
                position: 2,
                name: "Servicios",
                item: `${SITE_URL}/servicios`,
              },
            ],
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            "@id": `${SITE_URL}/servicios#list`,
            name: "Servicios BTL en Lima",
            url: `${SITE_URL}/servicios`,
            numberOfItems: SERVICES.length,
            itemListElement: SERVICES.map((s, i) => ({
              "@type": "ListItem",
              position: i + 1,
              name: s.name,
              url: `${SITE_URL}${s.slug}`,
            })),
          }),
        },
      ],
    };
  },
});

function ServicesIndex() {
  return (
    <PageShell>
      <PageHero
        eyebrow="· Servicios BTL"
        h1="Servicios BTL para tu campaña: elige el formato que necesitas"
        intro="Explora nuestras siete líneas de servicio y revisa qué incluye cada una. Podemos combinar activaciones, personal promocional, difusión y eventos según el objetivo de tu campaña, con una coordinación común y un presupuesto definido."
      >
        <Link
          to="/cotizar"
          className="rounded-full bg-primary px-7 py-3.5 text-base font-semibold text-primary-foreground shadow-pop ring-4 ring-primary/15 transition-transform hover:scale-[1.02]"
        >
          Solicitar propuesta →
        </Link>
      </PageHero>

      <p className="mx-auto mb-12 max-w-2xl px-5 text-center text-sm text-secondary/60 md:px-8">
        ¿Necesitas entender el alcance antes de elegir un servicio? Revisa nuestra guía sobre{" "}
        <Link
          to="/blog/$slug"
          params={{ slug: "todo-lo-que-hace-una-agencia-btl-en-lima-por-tu-marca" }}
          className="font-semibold text-primary hover:underline"
        >
          qué hace una agencia BTL en Perú
        </Link>
        : servicios, proceso y factores del presupuesto. Si ya tienes definido el formato y vas a
        comparar propuestas, revisa qué verificar al contratar{" "}
        <Link
          to="/blog/$slug"
          params={{ slug: "empresa-btl-peru-que-buscar-antes-de-contratar" }}
          className="font-semibold text-primary hover:underline"
        >
          empresas de BTL en Perú
        </Link>
        : cobertura, responsables y condiciones de ejecución.
      </p>

      <section className="pb-24 pt-4">
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {SERVICES.map((s, i) => (
              <Link
                key={s.slug}
                to={s.slug}
                className="group flex flex-col rounded-3xl border border-border bg-background p-7 shadow-soft transition-all duration-300 hover:-translate-y-1 hover:border-primary/40"
              >
                <span className="font-display text-sm font-bold text-primary">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <h2 className="mt-5 font-display text-2xl font-bold text-secondary">{s.name}</h2>
                <p className="mt-3 text-secondary/70">{s.short}</p>
                <span className="mt-6 text-sm font-semibold text-primary">Ver servicio →</span>
              </Link>
            ))}
          </div>
          <p className="mt-8 max-w-2xl text-base leading-relaxed text-secondary/75">
            ¿Buscas personal para representar tu marca en una activación o punto de venta? Consulta
            el servicio de{" "}
            <Link
              to="/impulsadoras-promotoras"
              className="font-semibold text-primary hover:underline"
            >
              promotoras BTL e impulsadoras
            </Link>
            : selección por perfil, capacitación en producto y supervisión de la campaña.
          </p>
        </div>
      </section>
    </PageShell>
  );
}
